import { zodResolver } from '@hookform/resolvers/zod';
import type { Namespace, TFunction } from 'i18next';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { RadioList, RadioListItem } from '../ui/button-radio-list.tsx';
import { Button } from '../ui/button.tsx';
import { Heading } from '../ui/heading.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { uiFilterVisibility, type UiFilterVisibility } from './types/ui-filter-visibility.ts';
import type { VerticalFilterViewState } from './types/vertical-filter-view-state.ts';

export type ViewFormData = {
    id?: string;
    name: string;
    visibility: UiFilterVisibility;
};

function createFormSchema(t: TFunction<Namespace>) {
    return z.object({
        name: z
            .string()
            .trim()
            .min(1, { message: t('save_view_dialog__name_required', { ns: 'filters' }) })
            .max(60, { message: t('save_view_dialog__name_too_long', { ns: 'filters' }) }),
        visibility: z.enum(uiFilterVisibility),
    });
}

type FormSchema = z.infer<ReturnType<typeof createFormSchema>>;

export type VerticalFiltersHeaderProps = {
    state: VerticalFilterViewState;
    setState: (state: VerticalFilterViewState) => void;
    hasActiveFilters: boolean;
    showSaveViewButton: boolean;
    canShareFilterViews: boolean;
    onClearAll: () => void;
    viewFormData: ViewFormData;
    onViewFormDataChange: (data: ViewFormData | ((prev: ViewFormData) => ViewFormData)) => void;
    onValidationChange?: (isValid: boolean) => void;
};

export function VerticalFiltersHeader({
    state,
    setState,
    showSaveViewButton,
    canShareFilterViews,
    hasActiveFilters,
    onClearAll,
    viewFormData,
    onViewFormDataChange,
    onValidationChange,
}: VerticalFiltersHeaderProps) {
    const { t } = useTranslation('filters');

    const formSchema = createFormSchema(t);

    const { control, formState, reset } = useForm<FormSchema>({
        defaultValues: {
            name: viewFormData.name,
            visibility: viewFormData.visibility,
        },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const { isValid, errors } = formState;

    // Sync form with external viewFormData changes
    useEffect(() => {
        reset({
            name: viewFormData.name,
            visibility: viewFormData.visibility,
        });
    }, [viewFormData.name, viewFormData.visibility, reset]);

    useEffect(() => {
        if (state === 'save-view' || state === 'edit-view') {
            onValidationChange?.(isValid);
        }
    }, [isValid, state, onValidationChange]);

    const handleClearAll = () => {
        setState('default');
        onViewFormDataChange({
            name: '',
            visibility: 'only_me',
        });
        onClearAll();
    };

    switch (state) {
        case 'save-view':
        case 'edit-view':
            return (
                <div className="flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-neutral-100">
                        <Heading variant="h3" className="text-sm font-medium text-neutral-500">
                            {state === 'edit-view'
                                ? t('edit_view_dialog__title')
                                : t('save_view_dialog__title')}
                        </Heading>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 h-auto text-neutral-500 text-sm"
                            onClick={handleClearAll}
                        >
                            {t('save_view_dialog__clear_all')}
                        </Button>
                    </div>
                    <div className="flex flex-col px-4 py-3 gap-3 border-b border-neutral-100">
                        <div>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder={t('save_view_dialog__name_placeholder')}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            onViewFormDataChange((prev) => ({
                                                ...prev,
                                                name: e.target.value,
                                            }));
                                        }}
                                        className={errors.name ? 'border-red-500' : ''}
                                        autoFocus
                                    />
                                )}
                            />
                            {errors.name && (
                                <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>
                            )}
                        </div>
                        {canShareFilterViews && (
                            <div>
                                <Label className="text-sm font-medium text-neutral-900 mb-3 block">
                                    {t('save_view_dialog__visibility_label')}
                                </Label>
                                <Controller
                                    name="visibility"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioList
                                            value={field.value}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                                onViewFormDataChange((prev) => ({
                                                    ...prev,
                                                    visibility: value as UiFilterVisibility,
                                                }));
                                            }}
                                            className="flex flex-row gap-3 justify-between"
                                        >
                                            <RadioListItem
                                                value="only_me"
                                                label={t('save_view_dialog__visibility_only_me')}
                                            />
                                            <RadioListItem
                                                value="team"
                                                label={t('save_view_dialog__visibility_team')}
                                            />
                                        </RadioList>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>
            );

        default:
            return (
                <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                    <Heading variant="h3">{t('filter_modal__title')}</Heading>
                    {hasActiveFilters && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 h-auto text-neutral-500"
                                onClick={onClearAll}
                            >
                                {t('remove_all')}
                            </Button>
                            {showSaveViewButton && (
                                <>
                                    {' '}
                                    <div className="w-px h-4 bg-neutral-300 mx-1" />
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 h-auto"
                                        onClick={() => {
                                            setState('save-view');
                                        }}
                                    >
                                        {t('save_view')}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
    }
}
